import React from 'react';
import styled from 'styled-components';

const CoursesContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: flex-start;
    padding: 40px 20px;
    gap: 30px;
    background-color: #f8fafc;
    min-height: calc(100vh - 56px);
    width: 100%;
    box-sizing: border-box;

    @media (max-width: 600px) {
        padding: 20px 12px;
        gap: 16px;
    }
`;

const CardWrapper = styled.div`
    position: relative;
    width: 320px;
    height: 200px;

    @media (max-width: 600px) {
        width: 100%;
        height: 180px;
    }
`;

const CourseCard = styled.div`
    display: flex;
    flex-direction: column;
    background-color: #ffffff;
    border-radius: 16px;
    padding: 28px;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border: 1px solid #e2e8f0;
    cursor: pointer;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 4px;
        background: linear-gradient(90deg, #00205B, #004b8d);
    }

    h2 {
        margin: 0 0 12px 0;
        color: #00205B;
        font-size: 1.5rem;
        font-weight: 700;
        letter-spacing: -0.025em;
    }

    .course-title {
        color: #475569;
        font-size: 1rem;
        margin-bottom: 20px;
        line-height: 1.5;
        font-weight: 500;
    }

    .course-meta {
        margin-top: auto;
        display: flex;
        align-items: center;
        gap: 8px;
        color: #64748b;
        font-size: 0.875rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }
`;

const HoverOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 16px;
    overflow: hidden;
    opacity: 0;
    transition: opacity 0.3s ease;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr;
    gap: 2px;
    background: #3a1a1a;
    z-index: 2;

    ${CardWrapper}:hover & {
        opacity: 1;
    }
`;

const OverlayQuadrant = styled.button`
    background: #8B2332;
    border: none;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    cursor: pointer;
    transition: background 0.2s ease;
    padding: 12px;
    font-family: 'Roboto', sans-serif;

    &:hover {
        background: #a12d3e;
    }
`;

const OverlayIcon = styled.span`
    font-size: 1.6rem;
    filter: brightness(0) invert(1);
`;

const OverlayLabel = styled.span`
    color: #ffffff;
    font-size: 0.85rem;
    font-weight: 700;
    text-align: center;
    line-height: 1.2;
`;


interface CourseCardComponentProps {
    id: string;
    title: string;
    term: string;
    onAction: (action: string) => void;
    isPlaceholder?: boolean;
}

const CourseCardComponent: React.FC<CourseCardComponentProps> = ({ id, title, term, onAction, isPlaceholder }) => {
    if (isPlaceholder) {
        return (
            <CardWrapper>
                <CourseCard>
                    <h2>{id}</h2>
                    <p className="course-title">{title}</p>
                    <div className="course-meta"><span>{term}</span></div>
                </CourseCard>
            </CardWrapper>
        );
    }

    return (
        <CardWrapper>
            <CourseCard>
                <h2>{id}</h2>
                <p className="course-title">{title}</p>
                <div className="course-meta"><span>{term}</span></div>
            </CourseCard>
            <HoverOverlay>
                <OverlayQuadrant onClick={() => onAction('topic')}>
                    <OverlayIcon>&#129514;</OverlayIcon>
                    <OverlayLabel>Study by Topic</OverlayLabel>
                </OverlayQuadrant>
                <OverlayQuadrant onClick={() => onAction('exam')}>
                    <OverlayIcon>&#128220;</OverlayIcon>
                    <OverlayLabel>Study by Exam</OverlayLabel>
                </OverlayQuadrant>
                <OverlayQuadrant onClick={() => onAction('timed')}>
                    <OverlayIcon>&#9201;</OverlayIcon>
                    <OverlayLabel>Start Timed Exam</OverlayLabel>
                </OverlayQuadrant>
                <OverlayQuadrant onClick={() => onAction('resources')}>
                    <OverlayIcon>&#128214;</OverlayIcon>
                    <OverlayLabel>Resources</OverlayLabel>
                </OverlayQuadrant>
            </HoverOverlay>
        </CardWrapper>
    );
};

interface SpecificCourseProps {
    onCourseAction?: (courseId: string, action: string) => void;
}

const SpecificCourse: React.FC<SpecificCourseProps> = ({ onCourseAction }) => {
    const courses = [
        { id: 'MATH 101', title: 'Single Variable Calculus I', term: 'Fall 2025' },
        { id: 'MATH 102', title: 'Single Variable Calculus II', term: 'Spring 2026' },
        { id: 'MATH 212', title: 'Multivariable Calculus', term: 'Spring 2026' },
        { id: 'More courses coming soon!', title: '', term: '' },
    ];

    return (
        <CoursesContainer>
            {courses.map((course) => (
                <CourseCardComponent
                    key={course.id}
                    id={course.id}
                    title={course.title}
                    term={course.term}
                    isPlaceholder={course.id === 'More courses coming soon!'}
                    onAction={(action) => onCourseAction?.(course.id, action)}
                />
            ))}
        </CoursesContainer>
    );
};

export default SpecificCourse;
